import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import StatusBadge from '../components/StatusBadge';
import { AntragBadge } from '../components/Aufnahmeantrag';
import { fmtZahl, fmtEuro } from '../components/ZeitraumWahl';

function StatCard({ label, value, sub, color }) {
  return (
    <div className="stat-card" style={{ borderLeftColor: color || 'var(--accent)' }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value ?? '–'}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function formatDate(d) {
  return d ? new Date(d).toLocaleDateString('de-DE') : '–';
}

// ── Was heute ansteht ────────────────────────────────────────────────────────
function Heute({ daten }) {
  if (!daten) return null;
  const trainings = daten.trainings || [];
  const tag = new Date(`${daten.datum}T12:00:00Z`)
    .toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' });

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.5rem' }}>Heute – {tag}</h3>
      {trainings.length === 0 ? (
        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Heute ist kein Training eingeplant.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {trainings.map(t => {
            const erfasst = !!t.einheit_id;
            const zahl = t.art === 'leistung' ? t.teilnehmer_anzahl : t.anwesend;
            return (
              <div key={`${t.id}-${t.von}`} style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
                padding: '0.5rem 0.7rem', borderRadius: 8,
                background: erfasst ? '#f4faf4' : '#fff8e1',
                border: `1px solid ${erfasst ? '#c8e6c9' : '#ffe082'}`,
              }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem', minWidth: 92 }}>
                  {t.von?.slice(0, 5)}–{t.bis?.slice(0, 5)}
                </span>
                <Link to={t.art === 'leistung' ? '/leistungsgruppen' : `/gruppen/${t.id}`}
                  style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t.name}</Link>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', flex: 1 }}>
                  {t.trainer_name || 'kein Trainer hinterlegt'}
                </span>
                <span style={{
                  fontSize: '0.76rem', fontWeight: 700, borderRadius: 10, padding: '2px 9px',
                  background: erfasst ? '#e8f5e9' : '#fff3e0', color: erfasst ? '#2e7d32' : '#e65100',
                }}>
                  {erfasst
                    ? `✓ erfasst${zahl ? ` · ${zahl} Schwimmer` : ''}`
                    : 'noch nicht erfasst'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Wo es klemmt ─────────────────────────────────────────────────────────────
function Klemmt({ ueberbucht = [], ohneZeit = [] }) {
  if (ueberbucht.length === 0 && ohneZeit.length === 0) return null;
  return (
    <div className="card" style={{ marginBottom: '1rem', borderLeft: '4px solid #e65100' }}>
      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.5rem' }}>Das sollte jemand ansehen</h3>
      <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.86rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {ueberbucht.map(g => (
          <li key={`u${g.id}`}>
            <Link to={`/gruppen/${g.id}`}><strong>{g.name}</strong></Link> ist überbucht:
            {' '}{g.belegt} Kinder auf {g.max_teilnehmer} Plätzen.
          </li>
        ))}
        {ohneZeit.map(g => (
          <li key={`z${g.id}`}>
            <Link to={g.art === 'leistung' ? '/leistungsgruppen' : `/gruppen/${g.id}`}><strong>{g.name}</strong></Link>
            {' '}hat keine Trainingszeit – dort lässt sich kein Training und keine Stunde erfassen.
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/api/admin/dashboard')
      .then(r => setData(r.data))
      .catch(() => setError('Dashboard-Daten konnten nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-screen"><div className="loading-spinner" /></div>;
  if (error)   return <div className="alert alert-error">{error}</div>;

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Dashboard</h2>
      </div>

      {/* Zuerst das, was heute ansteht – danach erst die Zählwerke */}
      <Heute daten={data.heute} />
      <Klemmt ueberbucht={data.ueberbuchte_gruppen} ohneZeit={data.gruppen_ohne_zeit} />

      <div className="stat-grid">
        <StatCard label="Neue Wartelisteneinträge" value={data.warteliste.neu_gesamt}
          sub={`Heute: ${data.warteliste.neu_heute} · Woche: ${data.warteliste.neu_woche}`} color="#0099cc" />
        <StatCard label="Offene Kontaktanfragen" value={data.kontakt.neu_gesamt}
          sub={`Neue diese Woche: ${data.kontakt.neu_woche}`} color="#e65100" />
        <StatCard label="Offene Probetrainings" value={data.probetraining.neu_gesamt}
          sub={`Neue diese Woche: ${data.probetraining.neu_woche}`} color="#7b1fa2" />
        {data.quartal && (
          <StatCard label={`Stunden Q${data.quartal.quartal} ${data.quartal.jahr}`}
            value={fmtZahl(data.quartal.stunden)}
            sub={`Auszuzahlen: ${fmtEuro(data.quartal.betrag)}`} color="#2e7d32" />
        )}
      </div>

      {(data.aufnahmeantraege || []).length > 0 && (
        <div className="card" style={{ marginBottom: '1rem', borderLeft: `4px solid ${data.aufnahmeantraege.some(a => a.aufnahmeantrag_ueberfaellig) ? '#c62828' : '#e65100'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: 8, flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700 }}>
              Aufnahmeanträge offen ({data.aufnahmeantraege.length})
            </h3>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              {data.aufnahmeantraege.filter(a => a.aufnahmeantrag_ueberfaellig).length} überfällig ·{' '}
              {data.aufnahmeantraege.filter(a => a.aufnahmeantrag_status === 'beim_trainer').length} beim Trainer, Eingang bestätigen
            </span>
          </div>
          {data.aufnahmeantraege.map(a => (
            <Link key={a.id} to={`/schwimmer/${a.id}`} style={{
              padding: '7px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', gap: 8, color: 'inherit', textDecoration: 'none',
            }}>
              <div>
                <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{a.vorname} {a.nachname}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                  {a.gruppe_name || 'ohne Gruppe'}
                  {a.aufnahmeantrag_status === 'beim_trainer'
                    ? ` · beim Trainer${a.trainer_name ? ` (${a.trainer_name})` : ''} seit ${formatDate(a.aufnahmeantrag_trainer_am)}`
                    : ` · vorläufig seit ${formatDate(a.aufnahmeantrag_fehlt_seit)}`}
                </div>
              </div>
              <AntragBadge status={a.aufnahmeantrag_status} ueberfaellig={a.aufnahmeantrag_ueberfaellig} trainings={a.aufnahmeantrag_trainings} />
            </Link>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700 }}>Letzte Wartelisteneinträge</h3>
            <Link to="/warteliste" style={{ fontSize: '0.8rem', color: 'var(--accent)' }}>Alle anzeigen →</Link>
          </div>
          {data.letzte_warteliste.length === 0
            ? <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Keine Einträge</p>
            : data.letzte_warteliste.map(e => (
              <div key={e.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{e.schwimmer_vorname} {e.schwimmer_nachname}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{formatDate(e.created_at)}</div>
                </div>
                <StatusBadge status={e.status} type="warteliste" />
              </div>
            ))
          }
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700 }}>Letzte Kontaktanfragen</h3>
            <Link to="/kontakt" style={{ fontSize: '0.8rem', color: 'var(--accent)' }}>Alle anzeigen →</Link>
          </div>
          {data.letzte_kontakt.length === 0
            ? <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Keine Einträge</p>
            : data.letzte_kontakt.map(e => (
              <div key={e.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{e.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{e.betreff || '(kein Betreff)'} · {formatDate(e.created_at)}</div>
                </div>
                <StatusBadge status={e.status} type="kontakt" />
              </div>
            ))
          }
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700 }}>Letzte Probetraining-Anfragen</h3>
            <Link to="/probetraining" style={{ fontSize: '0.8rem', color: 'var(--accent)' }}>Alle anzeigen →</Link>
          </div>
          {data.letzte_probetraining.length === 0
            ? <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Keine Einträge</p>
            : data.letzte_probetraining.map(e => (
              <div key={e.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{e.schwimmer_name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{e.gewuenschter_bereich || '–'} · {formatDate(e.created_at)}</div>
                </div>
                <StatusBadge status={e.status} type="probetraining" />
              </div>
            ))
          }
        </div>

      </div>
    </div>
  );
}
